import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiSave, FiX } from 'react-icons/fi';
import { gsidaPartnersApi, fileUploadApi } from '../../api/phpApi';
import './AdminManage.css';

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    website: '',
    description: '',
    partner_type: ''
  });

  // Fetch partners
  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      setLoading(true);
      const data = await gsidaPartnersApi.getAll();
      setPartners(data || []);
    } catch (err) {
      setError('Failed to load partners: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const url = await fileUploadApi.uploadImage(file, 'images', 'partners');
      setFormData(prev => ({ ...prev, logo: url }));
      setLogoPreview(url);
    } catch (err) {
      setError('Failed to upload logo: ' + err.message);
    }
  };

  // Handle add/edit submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await gsidaPartnersApi.update(editingId, formData);
      } else {
        await gsidaPartnersApi.create(formData);
      }
      resetForm();
      loadPartners();
    } catch (err) {
      setError('Failed to save partner: ' + err.message);
    }
  };

  // Handle edit
  const handleEdit = (partner) => {
    setFormData(partner);
    setLogoPreview(partner.logo);
    setEditingId(partner.id);
    setIsFormOpen(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this partner?')) {
      try {
        await gsidaPartnersApi.delete(id);
        loadPartners();
      } catch (err) {
        setError('Failed to delete partner: ' + err.message);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      logo: '',
      website: '',
      description: '',
      partner_type: ''
    });
    setLogoPreview('');
    setEditingId(null);
    setIsFormOpen(false);
  };

  if (loading) return <div className="admin-loading">Loading partners...</div>;

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Manage GSIDA Partners</h2>
        <button 
          className="btn-primary btn-add"
          onClick={() => setIsFormOpen(true)}
        >
          <FiPlus /> Add Partner
        </button>
      </div>

      {error && <div className="error-alert">{error}</div>}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Edit Partner' : 'Add New Partner'}</h3>
              <button className="btn-close" onClick={resetForm}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group form-row full">
                <label>Partner Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Partner Type</label>
                  <select
                    name="partner_type"
                    value={formData.partner_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select type</option>
                    <option value="Institutional">Institutional</option>
                    <option value="Strategic">Strategic</option>
                    <option value="Academic">Academic</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>
              </div>

              <div className="form-group form-row full">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Partner Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
                {logoPreview && (
                  <img src={logoPreview} alt="Logo Preview" className="image-preview" />
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <FiSave /> Save Partner
                </button>
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Partners Table */}
      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Partner Name</th>
              <th>Type</th>
              <th>Website</th>
              <th>Logo</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {partners.length === 0 ? (
              <tr><td colSpan="5" className="text-center">No partners found</td></tr>
            ) : (
              partners.map(partner => (
                <tr key={partner.id}>
                  <td>{partner.name}</td>
                  <td><span style={{ backgroundColor: '#d1fae5', padding: '4px 12px', borderRadius: '4px', fontSize: '0.85rem' }}>{partner.partner_type || 'General'}</span></td>
                  <td>{partner.website ? <a href={partner.website} target="_blank" rel="noopener noreferrer" style={{ color: '#0A66C2' }}>Visit</a> : 'N/A'}</td>
                  <td>{partner.logo ? '✓' : '—'}</td>
                  <td className="actions">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(partner)}
                      title="Edit"
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(partner.id)}
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
